import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Grid,
    CircularProgress,
    Alert,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function EmployerJobDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);

    const { data: job, isLoading, isError, error } = useQuery(
        ['employerJob', id],
        () => JobHttpService.loadJob(id),
        { enabled: !!id }
    );

    const { data: applicationsData, isLoading: applicationsLoading } = useQuery(
        ['jobApplications', id],
        () => JobHttpService.loadJobApplications({ jobId: id }),
        { enabled: !!id }
    );

    const applications = applicationsData?.results || applicationsData || [];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleApplicationClick = (applicationId) => {
        navigate(`/employer/applications/${applicationId}`);
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError || !job) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">
                        {error?.message || 'Failed to load job details'}
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/employer/jobs')}
                        sx={{ mb: 3 }}
                    >
                        Back to Jobs
                    </Button>

                    {/* Job Header */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h4" fontWeight={600} gutterBottom>
                                {job.title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {job.company && (
                                    <Chip
                                        icon={<BusinessIcon />}
                                        label={job.company.title}
                                        size="small"
                                    />
                                )}
                                {job.address && (
                                    <Chip
                                        icon={<LocationOnIcon />}
                                        label={job.address}
                                        size="small"
                                    />
                                )}
                                {job.jobtypes && job.jobtypes.length > 0 && (
                                    <Chip
                                        icon={<WorkIcon />}
                                        label={job.jobtypes.map(jt => jt.title).join(', ')}
                                        size="small"
                                    />
                                )}
                                {job.experience_level && (
                                    <Chip
                                        label={job.experience_level}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                            {job.description && (
                                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                                    {job.description}
                                </Typography>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                {job.pay_minimum && job.pay_maximum && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Salary: ${job.pay_minimum} - ${job.pay_maximum} {job.pay_rate || ''}
                                        </Typography>
                                    </Grid>
                                )}
                                {job.created_at && (
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarTodayIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Posted: {new Date(job.created_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Tabs */}
                    <Card>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleTabChange}>
                                <Tab icon={<DescriptionIcon />} iconPosition="start" label="Job Details" />
                                <Tab 
                                    icon={<PeopleIcon />} 
                                    iconPosition="start" 
                                    label={`Applications (${applications.length})`} 
                                />
                            </Tabs>
                        </Box>

                        {/* Job Details Tab */}
                        <TabPanel value={tabValue} index={0}>
                            <CardContent>
                                <Grid container spacing={3}>
                                    {job.salary && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Salary
                                            </Typography>
                                            <Typography variant="body1">
                                                {job.salary}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {job.number_of_hire && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Number of Positions
                                            </Typography>
                                            <Typography variant="body1">
                                                {job.number_of_hire}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {job.contact_email && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Contact Email
                                            </Typography>
                                            <Typography variant="body1">
                                                {job.contact_email}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {job.start_date && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Start Date
                                            </Typography>
                                            <Typography variant="body1">
                                                {new Date(job.start_date).toLocaleDateString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {job.recruitment_timeline && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Recruitment Timeline
                                            </Typography>
                                            <Typography variant="body1">
                                                {job.recruitment_timeline}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {job.schedules && job.schedules.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Schedule
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {job.schedules.map((schedule) => (
                                                    <Chip key={schedule.id} label={schedule.title} size="small" />
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                    {job.benefits && job.benefits.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Benefits
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {job.benefits.map((benefit) => (
                                                    <Chip key={benefit.id} label={benefit.title} size="small" color="primary" />
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </TabPanel>

                        {/* Applications Tab */}
                        <TabPanel value={tabValue} index={1}>
                            <CardContent>
                                {applicationsLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : applications.length === 0 ? (
                                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                                        <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            No applications yet
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Applications will appear here when candidates apply for this job.
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Candidate</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Applied Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {applications.map((application) => (
                                                    <TableRow key={application.id} hover>
                                                        <TableCell>
                                                            {application.user?.profile?.first_name || application.user?.email?.split('@')[0] || 'Unknown'}
                                                            {application.user?.profile?.last_name && ` ${application.user.profile.last_name}`}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.user?.email || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.created_at 
                                                                ? new Date(application.created_at).toLocaleDateString()
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={application.final_decision || 'Pending'}
                                                                size="small"
                                                                color={
                                                                    application.final_decision === 'hired' ? 'success' :
                                                                    application.final_decision === 'shortlisted' ? 'primary' :
                                                                    application.final_decision === 'rejected' ? 'error' :
                                                                    'default'
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleApplicationClick(application.id)}
                                                            >
                                                                View Details
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </TabPanel>
                    </Card>
                </Container>
            </section>
    );
}

export default EmployerJobDetailsPage;

